import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

type HeroCarouselProps = {
  movies: Movie[];
};

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const total = movies.length;

  const go = useCallback((next: number) => setIndex((next + total) % total), [total]);

  useEffect(() => {
    if (total <= 1) return;
    const timer = window.setInterval(() => setIndex((v) => (v + 1) % total), 5500);
    return () => window.clearInterval(timer);
  }, [total]);

  if (total === 0) return null;
  const active = movies[index];

  return (
    <section className="relative w-full h-[520px] md:h-[600px] bg-foreground-950 overflow-hidden">
      {movies.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={movie.cover}
            alt={`${movie.title} 主视觉`}
            title={`${movie.title} - 八戒影视焦点推荐`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/50 to-foreground-950/40"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 h-full flex flex-col justify-end pb-14 md:pb-20">
        <h1 className="text-sm md:text-base font-bold text-accent-400 tracking-wide mb-3">
          八戒影视 · 最新电影电视剧资讯推荐平台
        </h1>
        <span className="inline-flex items-center gap-1.5 w-fit px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium mb-4">
          <i className="ri-fire-line"></i>
          本周焦点推荐
        </span>
        <h2 className="text-3xl md:text-5xl font-bold text-background-50 max-w-3xl leading-tight">
          {active.title}
        </h2>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 text-sm text-background-100">
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-400"></i>
            <strong className="text-accent-400">{active.rating.toFixed(1)}</strong>
          </span>
          <span>{active.type}</span>
          <span>{active.year}</span>
          <span>{active.country}</span>
          <span className="hidden sm:inline">{active.genres.join(" / ")}</span>
        </div>
        <p className="text-sm md:text-base text-background-200 max-w-2xl mt-3 line-clamp-2 md:line-clamp-3">
          {active.summary}
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-6">
          <Link
            to={`/detail/${active.id}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-play-circle-line text-lg"></i>
            查看详情
          </Link>
          <Link
            to={`/category/${active.type}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 hover:bg-background-50/25 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            更多{active.type}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="flex items-center gap-4 mt-8">
          <div className="flex items-center gap-2">
            {movies.map((movie, i) => (
              <button
                key={movie.id}
                type="button"
                aria-label={`切换到 ${movie.title}`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"
                }`}
              ></button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              aria-label="上一张"
              onClick={() => go(index - 1)}
              className="w-9 h-9 rounded-full border border-background-50/30 flex items-center justify-center text-background-50 hover:bg-background-50/15 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              type="button"
              aria-label="下一张"
              onClick={() => go(index + 1)}
              className="w-9 h-9 rounded-full border border-background-50/30 flex items-center justify-center text-background-50 hover:bg-background-50/15 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}